import { spotifyUrlFor } from "./spotifyShows";

/**
 * Inline "Listen on Spotify" CTA, scoped to a language.
 * - If a per-language show URL exists, link straight to it.
 * - Otherwise show a tasteful "coming soon" callout that nudges the visitor
 *   to subscribe via comments / the English feed in the meantime.
 *
 * `native`     — the language's native name, e.g. "Türkçe"
 * `code`       — ISO code, e.g. "tr"
 * `compact`    — render the small inline pill (used inside the reader)
 */
export default function SpotifyCTA({ code, native, compact = false }) {
  const url = spotifyUrlFor(code);
  const englishUrl = spotifyUrlFor("en");

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={"spotify-cta " + (compact ? "spotify-cta-compact" : "spotify-cta-full")}
        data-testid={`spotify-cta-${code}`}
      >
        <span className="spotify-cta-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M12 0a12 12 0 100 24 12 12 0 000-24zm5.5 17.3a.75.75 0 01-1 .25c-2.8-1.7-6.4-2-10.5-1.1a.75.75 0 11-.4-1.45c4.5-1 8.4-.6 11.6 1.3a.75.75 0 01.3 1zm1.5-3.3a.94.94 0 01-1.3.3c-3.2-2-8.1-2.6-11.9-1.4a.94.94 0 11-.55-1.8c4.4-1.3 9.8-.6 13.5 1.7a.94.94 0 01.25 1.2zm.15-3.4c-3.85-2.3-10.2-2.5-13.9-1.4a1.13 1.13 0 11-.65-2.15c4.25-1.3 11.3-1 15.7 1.6a1.13 1.13 0 11-1.15 1.95z"/>
          </svg>
        </span>
        <span className="spotify-cta-text">
          <span className="spotify-cta-label">Listen on Spotify</span>
          <span className="spotify-cta-lang">{native} edition</span>
        </span>
        <span className="spotify-cta-arrow" aria-hidden="true">↗</span>
      </a>
    );
  }

  // No language-specific show yet — soft fallback
  return (
    <div
      className={"spotify-soon " + (compact ? "spotify-soon-compact" : "")}
      data-testid={`spotify-soon-${code}`}
    >
      <span className="spotify-soon-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M12 0a12 12 0 100 24 12 12 0 000-24zm5.5 17.3a.75.75 0 01-1 .25c-2.8-1.7-6.4-2-10.5-1.1a.75.75 0 11-.4-1.45c4.5-1 8.4-.6 11.6 1.3a.75.75 0 01.3 1zm1.5-3.3a.94.94 0 01-1.3.3c-3.2-2-8.1-2.6-11.9-1.4a.94.94 0 11-.55-1.8c4.4-1.3 9.8-.6 13.5 1.7a.94.94 0 01.25 1.2zm.15-3.4c-3.85-2.3-10.2-2.5-13.9-1.4a1.13 1.13 0 11-.65-2.15c4.25-1.3 11.3-1 15.7 1.6a1.13 1.13 0 11-1.15 1.95z"/>
        </svg>
      </span>
      <span className="spotify-soon-text">
        Spotify <strong>{native}</strong> edition coming soon
        {englishUrl && (
          <>
            {" — "}
            <a
              href={englishUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`spotify-soon-en-fallback-${code}`}
            >
              listen in English meanwhile ↗
            </a>
          </>
        )}
      </span>
    </div>
  );
}
